import { apiJobs } from "@/api/recruit/job.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  useJobEditData,
  useJobEditForm,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Box, Button, FontAwesomeIcon, Stack, Switch, Textarea, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function JobEditAi() {
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useJobEditForm();

  const { getPayload, sections } = useJobEditData();
  const { handleErrors } = useErrorHandle();

  const { t } = useTranslation();
  const { isFeatureEnabled } = usePermissions();

  const useAI = watch("use_ai");

  const [isAIRequirementsLoading, setIsAIRequirementsLoading] = useState(false);

  const handleGenerateAIRequirements = async () => {
    setIsAIRequirementsLoading(true);
    const { data, error } = await apiJobs.generateRequirements({
      ...getPayload(),
      description: getValues("description"),
    });
    if (data) setValue("ai_requirements", data.ai_requirements || null);
    else handleErrors(error);
    setIsAIRequirementsLoading(false);
  };

  const hasDescription = sections.some((section) =>
    section.fields.some((field) => field.type === ApiFieldTypes.job_description)
  );

  if (!isFeatureEnabled(ApiFeatureType.ats_ai) || !hasDescription) return null;

  return (
    <Stack className={"card-panel"} gap={"2xl"}>
      <Title
        header={t("ats:ai_feature_title")}
        caption={t("ats:job_requirements_description")}
        size={"md"}
        headerIcon={<FontAwesomeIcon icon={"fa-light fa-wand-magic-sparkles"} />}
      />

      <Controller
        control={control}
        name={"use_ai"}
        render={({ field: { value, onChange } }) => (
          <Switch
            checked={value}
            onChange={onChange}
            title={t("ats:use_ai_title")}
            description={t("ats:use_ai_description")}
          />
        )}
      />
      {useAI && (
        <>
          <Box>
            <Button
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-wand-magic-sparkles"} />}
              onClick={handleGenerateAIRequirements}
              isLoading={isAIRequirementsLoading}
            >
              {t("common:generate_with_ai")}
            </Button>
          </Box>

          <Controller
            control={control}
            name="ai_requirements"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ""}
                label={t("ats:job_requirements_title")}
                placeholder={t("common:search_start_typing")}
                required={true}
                error={errors.ai_requirements?.message}
              />
            )}
          />
        </>
      )}
    </Stack>
  );
}
